/**
 * Dispatch 模块共享日期/时间工具（单一真相源）。
 * 在此统一 dayjs.extend(utc/timezone)，并提供模块内复用的格式化 helper，
 * 避免各文件重复 extend 和重复定义 fmtLong/relLabel/fmtTimeM/hhmm。
 *
 * 所有 today/now 判定一律以传入的 timezone（房源时区）为准，不用浏览器本地时间。
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { todayKey } from '@/utils/businessDay'
dayjs.extend(utc)
dayjs.extend(timezone)

export { dayjs }

/** "Wed, Jun 10"（房源时区） */
export function fmtLong(date: string, tz: string): string {
  return dayjs.tz(date, tz).format('ddd, MMM D')
}

/** Today / Tomorrow / "Wed 10"（房源时区） */
export function relLabel(date: string, tz: string): string {
  const today = todayStr(tz)
  const tomorrow = tomorrowStr(tz)
  if (date === today) return 'Today'
  if (date === tomorrow) return 'Tomorrow'
  return dayjs.tz(date, tz).format('ddd D')
}

/** 分钟数 → "HH:MM AM/PM" */
export function fmtTimeM(mins: number): string {
  const h24 = Math.floor(mins / 60)
  const mm = mins % 60
  const ap = h24 >= 12 ? 'PM' : 'AM'
  let h = h24 % 12
  if (h === 0) h = 12
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${ap}`
}

/** 分钟数 → "HH:MM"（24 小时） */
export function hhmm(mins: number): string {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}

/** "HH:MM"（24 小时，原生 <input type="time"> 的值）→ 当日 0 点起分钟数；非法返回 null */
export function parseHhmm(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (Number.isNaN(h) || Number.isNaN(min)) return null
  return h * 60 + min
}

/** 房源时区今天 YYYY-MM-DD */
export function todayStr(tz: string): string {
  return todayKey(tz)
}

/**
 * 「今天」派单的默认计划时间(当日 0 点起分钟数)= 现在 + 30 分钟,并**向上取整到下一个
 * 15 分钟整点**,夜间溢出夹到 23:59。
 *
 * 取整是为了给一个干净的默认值:例如现在 16:23 → +30 = 16:53 → 取整到 17:00,
 * 而不是落在 16:53 这种零碎时间。这是个「方便的默认」,不是强制约束——用户随时可改。
 */
export function defaultTodayTimeMin(tz: string): number {
  const n = dayjs().tz(tz).add(30, 'minute')
  const raw = n.hour() * 60 + n.minute()
  const rounded = Math.ceil(raw / 15) * 15
  return Math.min(rounded, 23 * 60 + 59)
}

/** 房源时区明天 YYYY-MM-DD */
export function tomorrowStr(tz: string): string {
  return dayjs().tz(tz).add(1, 'day').format('YYYY-MM-DD')
}
